/**
 * AuthContext — Singleton Auth State Provider
 * 
 * CRITICAL: This is the ONLY place where Supabase auth APIs are called.
 * 
 * Architecture guarantees:
 * 1. getSession() is called EXACTLY once on app startup
 * 2. onAuthStateChange() is registered EXACTLY once
 * 3. Session/user are stored in memory — components READ, never fetch
 * 4. Prevents auth request loops that caused quota exhaustion
 * 
 * DO NOT call supabase.auth.getUser() anywhere else in the app!
 * Use useAuthContext() hook or getCachedUserId() / getCachedSession() functions.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Session, User } from '@supabase/supabase-js';
import { logger } from '@/lib/services/logger';

// ==================== REACT AVAILABILITY CHECK ====================
// Defensive check - if React isn't properly loaded, fail fast with clear message
if (!React || typeof React.createContext !== 'function') {
  const err = new Error('[AuthContext] React is not available or createContext is missing');
  console.error(err);
  throw err;
}

// ==================== SINGLETON GUARD ====================

/**
 * Prevents multiple auth initializations.
 * If this becomes true more than once, we have a critical bug.
 */
let authInitialized = false;

// ==================== CACHED STATE (Module-level) ====================

/**
 * Module-level cache for synchronous access from services.
 * Updated by AuthProvider on every auth state change.
 */
let cachedSession: Session | null = null;
let cachedUser: User | null = null;

/**
 * Get cached user ID synchronously.
 * Use this in services/workers that need user ID without async calls.
 * 
 * IMPORTANT: Do NOT call supabase.auth.getUser() — use this instead!
 */
export function getCachedUserId(): string | null {
  return cachedUser?.id ?? null;
}

/**
 * Get cached session synchronously.
 * Use this in services that need access token for API calls.
 */
export function getCachedSession(): Session | null {
  return cachedSession;
}

/**
 * Get cached user synchronously.
 */
export function getCachedUser(): User | null {
  return cachedUser;
}

// ==================== TYPES ====================

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  authStatus: AuthStatus;
  /** True when initial auth check is complete */
  authReady: boolean;
}

// ==================== CONTEXT ====================

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ==================== PROVIDER ====================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const [authReady, setAuthReady] = useState(false);

  // Update module-level cache whenever state changes
  const updateCache = useCallback((newSession: Session | null, newUser: User | null) => {
    cachedSession = newSession;
    cachedUser = newUser;
  }, []);

  useEffect(() => {
    // ==================== SINGLETON CHECK ====================
    if (authInitialized) {
      if (import.meta.env.DEV) {
        console.error(
          '[AUTH GUARD] AuthProvider initialized more than once!\n' +
          'This will cause duplicate auth API calls and quota issues.\n' +
          'Ensure AuthProvider is mounted only once at the app root.'
        );
      }
      logger.error('AuthProvider double initialization detected', {
        context: { feature: 'auth', function_name: 'AuthProvider' },
        error_code: 'auth_double_init',
        error_type: 'unexpected',
      });
      // Don't return — allow it to work but log the issue
    }
    authInitialized = true;

    logger.info('AuthProvider initializing (singleton)', {
      context: { feature: 'auth', function_name: 'AuthProvider' },
    });

    // ==================== WATCHDOG TIMEOUT ====================
    // Safety net: if getSession() hangs (Safari/WebView/storage issues),
    // force authReady=true after timeout to unblock the app
    const AUTH_TIMEOUT_MS = 4000;
    let authCompleted = false;
    
    const watchdogTimer = setTimeout(() => {
      if (!authCompleted) {
        logger.warn('Auth check TIMEOUT - forcing unauthenticated state', {
          context: { feature: 'auth', function_name: 'AuthProvider' },
          data: { timeout_ms: AUTH_TIMEOUT_MS },
          allowList: ['timeout_ms'],
        });
        
        setSession(null);
        setUser(null);
        updateCache(null, null);
        setAuthStatus('unauthenticated');
        setAuthReady(true);
      }
    }, AUTH_TIMEOUT_MS);

    const markAuthCompleted = () => {
      authCompleted = true;
      clearTimeout(watchdogTimer);
    };

    // ==================== AUTH STATE LISTENER (FIRST) ====================
    // Set up listener BEFORE checking session to catch any auth events during init
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // Synchronous state updates only — no async calls here!
        const newUser = newSession?.user ?? null;
        
        setSession(newSession);
        setUser(newUser);
        updateCache(newSession, newUser);
        
        if (newSession) {
          setAuthStatus('authenticated');
        } else {
          setAuthStatus('unauthenticated');
        }

        logger.debug('Auth state changed', {
          context: { feature: 'auth', function_name: 'onAuthStateChange' },
          data: { event, hasSession: !!newSession },
          allowList: ['event', 'hasSession'],
        });
      }
    );

    // ==================== INITIAL SESSION CHECK (SECOND) ====================
    // This is the ONLY getSession() call in the entire app
    supabase.auth
      .getSession()
      .then(({ data: { session: initialSession } }) => {
        markAuthCompleted();
        
        const initialUser = initialSession?.user ?? null;

        setSession(initialSession);
        setUser(initialUser);
        updateCache(initialSession, initialUser);

        if (initialSession) {
          setAuthStatus('authenticated');
        } else {
          setAuthStatus('unauthenticated');
        }

        setAuthReady(true);

        logger.info('Initial auth check complete', {
          context: { feature: 'auth', function_name: 'AuthProvider' },
          data: { hasSession: !!initialSession },
          allowList: ['hasSession'],
        });
      })
      .catch((error) => {
        markAuthCompleted();
        
        logger.error('Initial session check failed', {
          context: { feature: 'auth', function_name: 'AuthProvider' },
          error,
          error_code: 'session_check_failed',
          error_type: 'network',
        });

        setAuthStatus('unauthenticated');
        setAuthReady(true);
      });

    // Cleanup
    return () => {
      clearTimeout(watchdogTimer);
      subscription.unsubscribe();
      // В dev-режиме StrictMode дважды вызывает эффекты — сбрасываем флаг
      // В production флаг остаётся true (singleton гарантия)
      if (import.meta.env.DEV) {
        authInitialized = false;
      }
    };
  }, [updateCache]);

  const value: AuthContextValue = {
    session,
    user,
    authStatus,
    authReady,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ==================== HOOK ====================

/**
 * Access auth state from components.
 * 
 * IMPORTANT: This reads cached state — no network calls!
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  
  // Graceful fallback for HMR race conditions in dev mode
  // Returns "loading" state instead of crashing when context is undefined
  if (context === undefined) {
    if (import.meta.env.DEV) {
      console.warn('[AuthContext] useAuthContext called outside AuthProvider - returning loading state (likely HMR)');
    }
    
    return {
      session: null,
      user: null,
      authStatus: 'loading',
      authReady: false,
    };
  }
  
  return context;
}

export default AuthContext;
